/**
 * Code-aware error resolver for `POST /api/admin/users/:id/test-account`
 * (ADR-0009 — prod test partition, Amendment 1).
 *
 * The 409 here is NOT a generic conflict: it is the load-bearing business
 * rule of the ADR. The flag moves TOGETHER with the `isTest` stamp on every
 * property and venue the account owns (one transaction on the API). Real →
 * test is always allowed; test → real is refused while an owned entity
 * carries demo activity (reviewer check-ins, demo passes, payout accruals,
 * subscriptions) — that activity must never enter the real partition.
 *
 *   ACCOUNT_HAS_ACTIVITY — the current API (Amendment 1): test → real blocked
 *     by demo activity; `details` names the entities with their counts.
 *   ACCOUNT_HAS_ENTITIES — the pre-amendment API (flag could not move under
 *     ANY owned entity). Still mapped so a backoffice deployed ahead of the
 *     API keeps explaining the rule the server is actually enforcing.
 *
 * The operator therefore needs the *rule*, not a generic "conflict" toast.
 * Hence a dedicated resolver rather than the status-only
 * `resolveMutationErrorMessage`.
 *
 * Side-effect free — unit-tested in `test-account-error.test.ts`.
 */
import { ApiError } from "@/shared/api/http-client";
import { extractApiErrorMessage } from "@/shared/api/error-message";

type Translate = (key: string, values?: Record<string, unknown>) => string;

const ACCOUNT_HAS_ACTIVITY = "ACCOUNT_HAS_ACTIVITY";
const ACCOUNT_HAS_ENTITIES = "ACCOUNT_HAS_ENTITIES";
const USER_NOT_FOUND = "USER_NOT_FOUND";

/** Named entities blocking the toggle, as reported in the 409 `details`. */
export interface BlockingEntities {
  readonly properties: readonly string[];
  readonly venues: readonly string[];
}

function errorCodeOf(err: unknown): string | null {
  if (!(err instanceof ApiError)) return null;
  const body = err.body;
  if (!body || typeof body !== "object") return null;
  const code = (body as { errorCode?: unknown }).errorCode;
  return typeof code === "string" && code.length > 0 ? code : null;
}

/**
 * True for either business 409 (current `ACCOUNT_HAS_ACTIVITY`, legacy
 * `ACCOUNT_HAS_ENTITIES`) — both carry a `details` payload naming entities.
 */
export function isBlockingEntitiesError(err: unknown): boolean {
  const code = errorCodeOf(err);
  return code === ACCOUNT_HAS_ACTIVITY || code === ACCOUNT_HAS_ENTITIES;
}

/** Pull `[{ id, name, … }]` lists out of the 409 body, tolerating any shape. */
function namesFrom(value: unknown): readonly string[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((entry) =>
      entry && typeof entry === "object" ? (entry as { name?: unknown }).name : undefined,
    )
    .filter((name): name is string => typeof name === "string" && name.trim().length > 0);
}

/**
 * The properties/venues the API refused on. Naming them turns an abstract
 * rule into something the operator can act on — they're the exact entities
 * carrying demo activity (or, on the legacy API, the ones that would have to
 * be deleted).
 *
 * `details` is best-effort: the rule is explained either way.
 */
export function blockingEntities(err: unknown): BlockingEntities {
  if (!(err instanceof ApiError)) return { properties: [], venues: [] };
  const body = err.body;
  if (!body || typeof body !== "object") return { properties: [], venues: [] };

  const details = (body as { details?: unknown }).details;
  if (!details || typeof details !== "object") return { properties: [], venues: [] };

  return {
    properties: namesFrom((details as { properties?: unknown }).properties),
    venues: namesFrom((details as { venues?: unknown }).venues),
  };
}

export function resolveTestAccountError(err: unknown, t: Translate): string {
  const code = errorCodeOf(err);

  // Our own copy, not the server's: the API's message is English-only and
  // talks about internals. The operator needs the consequence.
  if (code === ACCOUNT_HAS_ACTIVITY) return t("users.testAccount.errors.hasActivity");
  if (code === ACCOUNT_HAS_ENTITIES) return t("users.testAccount.errors.hasEntities");

  if (err instanceof ApiError) {
    if (err.status === 401 || err.status === 403) return t("users.errors.forbidden");
    if (err.status === 404 || code === USER_NOT_FOUND) {
      return t("users.testAccount.errors.notFound");
    }

    // Unmapped 4xx (e.g. the 400 when `isTestAccount` isn't a boolean) —
    // surface the server's own reason rather than swallow it.
    if (err.status >= 400 && err.status < 500) {
      const serverMessage = extractApiErrorMessage(err);
      if (serverMessage) return t("users.errors.serverPrefix", { message: serverMessage });
    }
  }

  return t("users.testAccount.errors.generic");
}
